import axios from 'axios';

import type { IncidentConfigurationDraftSummaryList } from './types';

interface DraftsInvalidatedBySaveParams {
  organizationId: number;
  incidentConfigurationId: number;
  liveVersion: number;
  // The draft the editor was opened from, when the save is promoting it to live. It matches the
  // predicate below (a resumable draft is always based on the current live version), but losing it
  // is the point of the promotion rather than collateral damage, so it must not trigger a warning.
  excludedDraftId?: number | null;
}

/**
 * Whether saving the live configuration would make any of its saved drafts unavailable.
 *
 * A live save bumps `IncidentConfiguration.version` by one, and the server then invalidates every
 * draft of that configuration whose `base_version` is below the bumped value. The same predicate is
 * mirrored here so the warning covers exactly the drafts the save is about to take away. Drafts
 * invalidated by an earlier save are already omitted from the list endpoint, so every row it returns
 * is still resumable.
 *
 * Known false positive: the server only bumps the version when the PATCH is a real edit
 * (`apply_incident_configuration_update`), so pressing SAVE without changing anything warns and then
 * invalidates nothing. Deciding that client-side would mean re-implementing the server's diff, so
 * the warning deliberately errs towards over-warning.
 */
export const hasDraftsInvalidatedBySave = async ({
  organizationId,
  incidentConfigurationId,
  liveVersion,
  excludedDraftId,
}: DraftsInvalidatedBySaveParams): Promise<boolean> => {
  const { data } = await axios.get<IncidentConfigurationDraftSummaryList>(
    `/sysconfig/api/v1/organizations/${organizationId}/incident_configuration_drafts`
  );
  const versionAfterSave = liveVersion + 1;

  return (data ?? []).some(
    (draft) =>
      draft.incident_configuration_id === incidentConfigurationId &&
      draft.id !== excludedDraftId &&
      draft.base_version < versionAfterSave
  );
};
